/**
 * Payout Rates Service - Firestore Backend
 * 
 * Manages payout rates in Firestore instead of localStorage.
 * Ensures data consistency and enables centralized updates.
 */
import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  deleteDoc,
  query,
  where,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { LoanType } from '@/types';

export interface PayoutSlab {
  range: string;
  payout: string;
}

export interface PayoutRate {
  id: string;
  location?: string;
  lender: string;
  loanType: LoanType;
  maxPayout: string;
  dsaCode: string;
  slabs: PayoutSlab[];
  remarks?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy?: string;
  updatedBy?: string;
}

const PAYOUTS_COLLECTION = 'payoutRates';

/**
 * Get all payout rates
 */
export async function getAllPayoutRates(): Promise<PayoutRate[]> {
  const snapshot = await getDocs(collection(db, PAYOUTS_COLLECTION));
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as PayoutRate[];
}

/**
 * Get payout rates filtered by lender
 */
export async function getPayoutRatesByLender(lender: string): Promise<PayoutRate[]> {
  const q = query(
    collection(db, PAYOUTS_COLLECTION),
    where('lender', '==', lender)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as PayoutRate[];
}

/**
 * Get payout rates filtered by loan type
 */
export async function getPayoutRatesByLoanType(loanType: LoanType): Promise<PayoutRate[]> {
  const q = query(
    collection(db, PAYOUTS_COLLECTION),
    where('loanType', '==', loanType)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  })) as PayoutRate[];
}

/**
 * Get a specific payout rate by ID
 */
export async function getPayoutRateById(id: string): Promise<PayoutRate | null> {
  const docRef = doc(db, PAYOUTS_COLLECTION, id);
  const docSnap = await getDoc(docRef);
  
  if (!docSnap.exists()) {
    return null;
  }
  
  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as PayoutRate;
}

/**
 * Create or update a payout rate
 */
export async function savePayoutRate(
  data: Omit<PayoutRate, 'id' | 'createdAt' | 'updatedAt'>,
  userId: string,
  id?: string
): Promise<string> {
  const now = Timestamp.now();
  
  const payoutData = {
    ...data,
    updatedAt: now,
    updatedBy: userId,
  };

  if (id) {
    // Update existing
    const docRef = doc(db, PAYOUTS_COLLECTION, id);
    await setDoc(docRef, payoutData, { merge: true });
    return id;
  } else {
    // Create new
    const newDocRef = doc(collection(db, PAYOUTS_COLLECTION));
    await setDoc(newDocRef, {
      ...payoutData,
      createdAt: now,
      createdBy: userId,
    });
    return newDocRef.id;
  }
}

/**
 * Delete a payout rate
 */
export async function deletePayoutRate(id: string): Promise<void> {
  await deleteDoc(doc(db, PAYOUTS_COLLECTION, id));
}

/**
 * Seed initial payout data from payoutsData.ts
 * ADMIN ONLY - Run once during migration
 */
export async function seedPayoutRates(
  payoutsData: Array<{
    lender: string;
    loanType: LoanType;
    maxPayout: string;
    dsaCode: string;
    slabs: PayoutSlab[];
    remarks?: string;
  }>,
  adminUserId: string
): Promise<{ success: number; errors: number }> {
  const batch = writeBatch(db);
  const now = Timestamp.now();
  
  let success = 0;
  let errors = 0;

  for (const payout of payoutsData) {
    try {
      const newDocRef = doc(collection(db, PAYOUTS_COLLECTION));
      batch.set(newDocRef, {
        ...payout,
        createdAt: now,
        updatedAt: now,
        createdBy: adminUserId,
        updatedBy: adminUserId,
      });
      success++;
    } catch (error) {
      console.error('Failed to seed payout:', payout, error);
      errors++;
    }
  }

  await batch.commit();
  
  return { success, errors };
}
